import { memo, ReactElement, useCallback, useEffect, useRef } from 'react';
import { ITextInput } from '.';
import { digitsOnlyFieldValue, isDigitsOnlyKeyDown } from '@helper/utils';
import { useScrollIntoView } from '@hooks/usescrollIntoView';

const TextInput = (props: ITextInput): ReactElement => {
	const {
		id,
		type,
		name,
		value,
		label,
		error,
		required,
		disabled,
		setFieldValue,
		onBlur,
		maxLength,
		onKeyDown,
		inputMode,
		pattern,
		className,
		placeholder,
		isAutoFocus,
		onChange,
		dataAttributes,
		labelClass,
		revalidate,
		revalidateError,
	} = props;
	const inputRef = useRef<HTMLInputElement>(null);
	const { handleFocus } = useScrollIntoView(inputRef);
	const digitsOnlyField = isDigitsOnlyKeyDown(onKeyDown);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			const nextValue = digitsOnlyFieldValue(e.target.value, onKeyDown);
			if (e.target.value !== nextValue) {
				e.target.value = nextValue;
			}
			name && setFieldValue && setFieldValue(name, nextValue);

			if (onChange && name) {
				onChange(e);
			}
		},
		[name, onChange, onKeyDown, setFieldValue]
	);

	useEffect(() => {
		if (isAutoFocus && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isAutoFocus]);

	const dataProps: { [key: string]: string } = {};
	if (dataAttributes) {
		Object.keys(dataAttributes).forEach((key) => {
			dataProps[`data-${key}`] = dataAttributes[key];
		});
	}

	return (
		<>
			<label htmlFor={id} className={`${required ? 'required' : ''} ${labelClass ?? ''}`}>
				{label}
			</label>
			<input
				id={id}
				name={name}
				type={type}
				value={value}
				ref={inputRef}
				onBlur={onBlur}
				onFocus={handleFocus}
				onChange={handleChange}
				disabled={disabled}
				autoComplete="off"
				maxLength={maxLength}
				inputMode={inputMode ?? (digitsOnlyField ? 'numeric' : undefined)}
				pattern={pattern ?? (digitsOnlyField ? '[0-9]*' : undefined)}
				onKeyDown={onKeyDown}
				className={className}
				placeholder={placeholder}
				{...dataProps}
			/>
			{error && <p className={`error-message ${error && 'show'}`}>{error}</p>}
			{revalidate && <p className={`error-message ${revalidateError && 'show'}`}>{revalidateError}</p>}
		</>
	);
};

export default memo(TextInput);
